import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Parallax } from "react-scroll-parallax";
import { useCart } from "../cart/CartContext";
import { useSiteConfig, resolveImageUrl } from "../config/SiteConfigContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { RichText } from "../components/RichText";
import { ga4 } from "../analytics/ga4";
import { usePageTitle } from "../app/pageTitle";
import { productPath, pathFor } from "../app/paths";

gsap.registerPlugin(ScrollTrigger);

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";

interface CompositionItem { name: string; description: string; nutrition?: string; image_url?: string; }
interface ConfezioneData {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  box_includes_note?: string | null;
  long_description?: string | null;
  return_policy?: string | null;
  shipping_info?: string | null;
  allergens?: string | null;
  price_cents: number;
  currency: string;
  weight_grams?: number | null;
  dimensions?: string | null;
  moq?: number | null;
  media: { id: string; type: string; url: string }[];
  composition?: { items: CompositionItem[] } | null;
  collection?: { id: string; title: string; slug: string; theme_color?: string | null } | null;
}

interface RelatedConfezione {
  id: string; title: string; slug: string; description: string | null;
  price_cents: number; media: { id: string; type: string; url: string }[];
}

/* ── Strip HTML for compact inline display ── */
function stripHtml(html: string): string {
  if (!html) return "";
  if (!/<[a-z][\s\S]*>/i.test(html)) return html;
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
}

/* ── Accordion row ── */
function InfoAccordion({ title, content, cfg, isMobile }: {
  title: string;
  content: string;
  cfg: ReturnType<typeof useSiteConfig>;
  isMobile: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          width: "100%", padding: isMobile ? "18px 0" : "22px 0",
          border: "none", background: "transparent", cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: 0.5, color: cfg.color_dark }}>
          {title}
        </span>
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke={cfg.color_muted} strokeWidth="2"
          style={{ transition: "transform 0.3s ease", transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <div style={{
        maxHeight: open ? 400 : 0,
        overflow: "hidden",
        transition: "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      }}>
        <RichText html={content} style={{
          fontSize: 14, color: "rgba(28,75,59,0.86)", lineHeight: 1.85,
          paddingBottom: isMobile ? 18 : 22,
        }} />
      </div>
    </div>
  );
}

export function ConfezioneDetail() {
  const { t } = useTranslation();
  const { locale, slug } = useParams();
  const { addItem } = useCart();
  const cfg = useSiteConfig();
  const isMobile = useIsMobile();
  const [confezione, setConfezione] = useState<ConfezioneData | null>(null);
  const [related, setRelated] = useState<RelatedConfezione[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [added, setAdded] = useState(false);
  const [qty, setQty] = useState(1);
  const [activeThumb, setActiveThumb] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Titolo pagina = nome prodotto (già localizzato dall'API). "Nome - Granborgo".
  usePageTitle(confezione?.title);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setActiveThumb(0);
    setQty(1);
    fetch(`${API_BASE}/api/v1/shop/products/${slug}?lang=${locale || "it"}`)
      .then((r) => { if (!r.ok) throw new Error("not found"); return r.json(); })
      .then((data: ConfezioneData) => {
        setConfezione(data);
        // P2.10 — parte dal MOQ (quantità minima d'ordine), se il pacco ne ha uno.
        setQty(data.moq && data.moq > 1 ? data.moq : 1);
        // GA4 view_item (item_id = slug del prodotto).
        ga4.viewItem({ item_id: data.slug || slug || "", item_name: data.title, price_cents: data.price_cents }, data.currency);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, locale]);

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/shop/products?lang=${locale || "it"}`)
      .then((r) => r.json())
      .then((all: any[]) => {
        const pool = all.filter((p: any) => p.slug !== slug);
        if (pool.length === 0) return;

        // Track recently shown slugs in sessionStorage to avoid repeating
        const STORAGE_KEY = "gb_recent_related";
        let recent: string[] = [];
        try { recent = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]"); } catch {}

        // Prefer products NOT recently shown; fallback to full pool if needed
        const fresh = pool.filter((p: any) => !recent.includes(p.slug));
        const candidates = fresh.length >= 3 ? fresh : pool;

        // Fisher-Yates shuffle
        for (let i = candidates.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
        }

        const selected = candidates.slice(0, 3);

        // Update recent list (keep last 6 shown)
        const newRecent = [...new Set([...selected.map((p: any) => p.slug), ...recent])].slice(0, 6);
        try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newRecent)); } catch {}

        setRelated(selected.map((p: any) => ({
          id: p.id, title: p.title, slug: p.slug, description: p.description,
          price_cents: p.price_cents,
          media: p.image_url ? [{ id: "0", type: "image", url: p.image_url }] : [],
        })));
      })
      .catch(() => {});
  }, [slug, locale]);

  /* ── GSAP animations ── */
  useLayoutEffect(() => {
    if (!confezione || !containerRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(".gs-cd-hero-item", { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 1, ease: "expo.out", stagger: 0.1 });

      gsap.utils.toArray<HTMLElement>(".gs-cd-section-hdr").forEach((el) => {
        gsap.fromTo(el, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 1.1, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 85%", once: true } });
      });

      gsap.utils.toArray<HTMLElement>(".gs-cd-comp-row").forEach((el, i) => {
        const fromLeft = i % 2 === 0;
        gsap.fromTo(el, { opacity: 0, x: fromLeft ? -60 : 60 }, { opacity: 1, x: 0, duration: 1.2, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 82%", once: true } });
      });

      gsap.utils.toArray<HTMLElement>(".gs-cd-info-section").forEach((el) => {
        gsap.fromTo(el, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.8, ease: "power3.out", scrollTrigger: { trigger: el, start: "top 88%", once: true } });
      });

      gsap.utils.toArray<HTMLElement>(".gs-cd-related").forEach((el) => {
        gsap.fromTo(el.children, { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: 0.9, stagger: 0.12, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 82%", once: true } });
      });

      gsap.utils.toArray<HTMLElement>(".gs-cd-line").forEach((el) => {
        gsap.fromTo(el, { width: 0 }, { width: el.dataset.w || 48, duration: 1.2, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 90%", once: true } });
      });
    }, containerRef);
    return () => ctx.revert();
  }, [confezione, related]);

  const fmt = (cents: number) => (cents / 100).toFixed(2).replace(".", ",");

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <div style={{ width: 24, height: 24, border: "1.5px solid rgba(28,75,59,0.89)", borderTopColor: cfg.color_dark, borderRadius: "50%", animation: "spin .7s linear infinite" }} />
      </div>
    );
  }

  if (notFound || !confezione) {
    return (
      <div style={{ padding: isMobile ? "56px 24px" : "96px 48px", maxWidth: 1200, margin: "0 auto" }}>
        <p style={{ fontSize: 16, color: cfg.color_dark, marginBottom: 16 }}>{t("products.notFound")}</p>
        <Link to={pathFor("collections", locale)} style={{ color: cfg.color_primary, fontSize: 14, textDecoration: "none" }}>{t("productDetail.backToCollections")}</Link>
      </div>
    );
  }

  const images = confezione.media.filter((m) => m.type === "image");
  const activeImage = images[activeThumb] || images[0];
  const compositionItems = confezione.composition?.items || [];
  const themeColor = confezione.collection?.theme_color || cfg.color_accent;
  const minQty = confezione.moq && confezione.moq > 1 ? confezione.moq : 1;

  return (
    <div ref={containerRef} style={{ background: cfg.color_body_bg, minHeight: "100vh", overflow: "hidden" }}>
      <style>{`
        @keyframes kenBurnsProduct { 0% { transform: scale(1); } 100% { transform: scale(1.05); } }
        /* P2.10 — input quantità manuale: nasconde le frecce native (c'è già lo stepper +/−) */
        .gb-qty-input::-webkit-outer-spin-button,
        .gb-qty-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .gb-qty-input { -moz-appearance: textfield; appearance: textfield; }
      `}</style>

      {/* ═══ HERO — Two columns ═══ */}
      <section style={{ maxWidth: 1280, margin: "0 auto", padding: isMobile ? "24px 20px 0" : "48px 48px 0" }}>
        {/* Breadcrumb */}
        <nav className="gs-cd-hero-item" style={{ fontSize: 11, color: "rgba(28,75,59,0.86)", marginBottom: isMobile ? 16 : 24, display: "flex", alignItems: "center", gap: 6, letterSpacing: 0.3 }}>
          <Link to={`/${locale}`} style={{ color: "rgba(28,75,59,0.86)", textDecoration: "none", transition: "color .2s" }} onMouseEnter={(e) => { e.currentTarget.style.color = cfg.color_dark; }} onMouseLeave={(e) => { e.currentTarget.style.color = "#aaa"; }}>Home</Link>
          <span style={{ color: "rgba(28,75,59,0.89)" }}>/</span>
          <Link to={pathFor("collections", locale)} style={{ color: "rgba(28,75,59,0.86)", textDecoration: "none", transition: "color .2s" }} onMouseEnter={(e) => { e.currentTarget.style.color = cfg.color_dark; }} onMouseLeave={(e) => { e.currentTarget.style.color = "#aaa"; }}>{t("nav.collezioni")}</Link>
          <span style={{ color: "rgba(28,75,59,0.89)" }}>/</span>
          <span style={{ color: cfg.color_dark, fontWeight: 500 }}>{confezione.title}</span>
        </nav>

        <div style={{ display: isMobile ? "block" : "grid", gridTemplateColumns: "1.15fr 1fr", gap: 48, alignItems: "start" }}>
          {/* ── Image gallery ── */}
          <div className="gs-cd-hero-item">
            {/* Main image */}
            <div style={{ borderRadius: 0, overflow: "hidden", aspectRatio: "4/5", background: cfg.color_light_bg, marginBottom: images.length > 1 ? 12 : 0, position: "relative" }}>
              {activeImage ? (
                <img
                  key={activeImage.id}
                  src={resolveImageUrl(activeImage.url)}
                  alt={confezione.title}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", animation: "kenBurnsProduct 18s ease-in-out alternate infinite" }}
                />
              ) : (
                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ddd" strokeWidth="1"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21,15 16,10 5,21" /></svg>
                </div>
              )}
              {/* Collection badge */}
              {confezione.collection && (
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "32px 20px 16px", background: "linear-gradient(transparent, rgba(0,0,0,0.5))" }}>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase", color: themeColor, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: themeColor, display: "inline-block" }} />
                    {confezione.collection.title}
                  </span>
                </div>
              )}
            </div>
            {/* Thumbnails */}
            {images.length > 1 && (
              <div style={{ display: "flex", gap: 8 }}>
                {images.map((img, i) => (
                  <button
                    key={img.id}
                    onClick={() => setActiveThumb(i)}
                    style={{
                      width: isMobile ? 56 : 72, height: isMobile ? 56 : 72,
                      overflow: "hidden", border: i === activeThumb ? `2px solid ${themeColor}` : "2px solid transparent",
                      padding: 0, cursor: "pointer", background: cfg.color_light_bg,
                      transition: "border-color 0.3s",
                    }}
                  >
                    <img src={resolveImageUrl(img.url)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: i === activeThumb ? 1 : 0.5, transition: "opacity .3s" }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Info column ── */}
          <div style={{ position: isMobile ? "static" : "sticky", top: 100, display: "flex", flexDirection: "column", gap: 16, marginTop: isMobile ? 28 : 0 }}>

            {/* CARD 1: Main */}
            <div className="gs-cd-hero-item" style={{ background: cfg.color_light_bg, padding: isMobile ? "24px 20px" : "32px 28px" }}>
              <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 2.5, textTransform: "uppercase", color: themeColor, margin: "0 0 10px" }}>
                {t("collectionsIndex.giftBox")}
              </p>
              <h1 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 26 : 34, fontWeight: 400, color: cfg.color_dark, margin: "0 0 8px", lineHeight: 1.1, letterSpacing: -0.5 }}>
                {confezione.title}
              </h1>
              <p style={{ fontSize: isMobile ? 22 : 26, fontWeight: 400, fontFamily: cfg.font_heading, color: cfg.color_dark, margin: "0 0 4px" }}>
                &euro; {fmt(confezione.price_cents)}
              </p>
              <p style={{ fontSize: 11, color: "rgba(28,75,59,0.86)", margin: "0 0 20px", letterSpacing: 0.3 }}>
                {cfg.vat_rate_percent > 0
                  ? t("collectionDetail.vatExcluded", { rate: cfg.vat_rate_percent })
                  : t("collectionDetail.vatIncluded")}
              </p>

              {confezione.description && (
                <RichText html={confezione.description} style={{ fontSize: 14, color: "rgba(28,75,59,0.86)", lineHeight: 1.8, marginBottom: 24 }} />
              )}

              <div className="gs-cd-line" data-w="40" style={{ width: 40, height: 1, background: "rgba(0,0,0,0.08)", marginBottom: 20 }} />

              {/* Quantity + Add to cart */}
              <div style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
                <div style={{ display: "flex", alignItems: "center", border: "1px solid rgba(28,75,59,0.89)", overflow: "hidden", flexShrink: 0 }}>
                  <button onClick={() => setQty((q) => Math.max(minQty, q - 1))} style={{ width: 40, height: "100%", border: "none", background: "transparent", fontSize: 16, color: cfg.color_dark, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background .15s" }} onMouseEnter={(e) => { e.currentTarget.style.background = "#f5f5f5"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>&minus;</button>
                  {/* P2.10 — input numerico libero: si può digitare la quantità a mano (MOQ alto). */}
                  <input
                    type="number"
                    inputMode="numeric"
                    min={minQty}
                    step={1}
                    value={qty}
                    aria-label={t("productDetail.quantity")}
                    className="gb-qty-input"
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      setQty(Number.isNaN(n) ? 0 : Math.max(0, n));
                    }}
                    onBlur={() => setQty((q) => (q < minQty ? minQty : q))}
                    style={{ width: 52, height: 36, textAlign: "center", fontSize: 14, fontWeight: 500, color: cfg.color_dark, border: "none", borderLeft: "1px solid rgba(28,75,59,0.12)", borderRight: "1px solid rgba(28,75,59,0.12)", background: "transparent", outline: "none" }}
                  />
                  <button onClick={() => setQty((q) => Math.max(minQty, q) + 1)} style={{ width: 40, height: "100%", border: "none", background: "transparent", fontSize: 16, color: cfg.color_dark, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background .15s" }} onMouseEnter={(e) => { e.currentTarget.style.background = "#f5f5f5"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>+</button>
                </div>
                <button
                  onClick={() => {
                    const n = Math.max(minQty, qty);
                    setQty(n);
                    addItem({ id: confezione.id, title: confezione.title, slug: confezione.slug, price_cents: confezione.price_cents, currency: confezione.currency, image: activeImage?.url }, n);
                    setAdded(true);
                    setTimeout(() => setAdded(false), 2200);
                  }}
                  style={{ flex: 1, background: added ? themeColor : cfg.color_dark, color: "#fff", border: "none", padding: "14px 20px", fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer", transition: "background .3s", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                  onMouseEnter={(e) => { if (!added) e.currentTarget.style.background = "#333"; }}
                  onMouseLeave={(e) => { if (!added) e.currentTarget.style.background = added ? themeColor : cfg.color_dark; }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 01-8 0" /></svg>
                  {added ? t("cart.added") : t("products.addToCart")}
                </button>
              </div>

              {minQty > 1 && (
                <p style={{ fontSize: 11, color: "rgba(28,75,59,0.6)", margin: "10px 0 0", letterSpacing: 0.2 }}>
                  {t("productDetail.minOrderQty", { n: minQty })}
                </p>
              )}
            </div>

            {/* CARD 2: Composition summary */}
            {compositionItems.length > 0 && (
              <div className="gs-cd-hero-item" style={{ background: themeColor, padding: isMobile ? "24px 20px" : "28px 28px" }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: "#fff", margin: "0 0 14px" }}>
                  {t("productDetail.insideThisBox")}:
                </p>
                <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 10 }}>
                  {compositionItems.map((item, i) => (
                    <li key={i} style={{ fontSize: 13, color: "#fff", lineHeight: 1.55 }}>
                      <span style={{ fontWeight: 600, color: "#fff" }}>{item.name}</span>
                      {item.description && <span> — {stripHtml(item.description)}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CARD 3: Specs */}
            {(confezione.weight_grams || confezione.dimensions) && (
              <div className="gs-cd-hero-item" style={{ background: themeColor, padding: isMobile ? "18px 20px" : "20px 28px", display: "flex", gap: 20, flexWrap: "wrap" }}>
                {confezione.weight_grams && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                    <span style={{ fontSize: 12, color: "rgba(255,255,255,0.85)" }}>{confezione.weight_grams} g</span>
                  </div>
                )}
                {confezione.dimensions && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /></svg>
                    <span style={{ fontSize: 12, color: "rgba(255,255,255,0.85)" }}>{confezione.dimensions} cm</span>
                  </div>
                )}
              </div>
            )}

            {/* Box includes note */}
            <div className="gs-cd-hero-item" style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: isMobile ? "16px 20px" : "18px 28px", background: cfg.color_light_bg, borderLeft: `3px solid ${themeColor}` }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={themeColor} strokeWidth="1.5" style={{ flexShrink: 0, marginTop: 2 }}>
                <path d="M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z" />
              </svg>
              <RichText html={confezione.box_includes_note || t("productDetail.boxIncludesNote")} style={{ fontSize: 13, color: cfg.color_dark, lineHeight: 1.6 }} />
            </div>

            {/* Disclaimer */}
            <div className="gs-cd-hero-item" style={{ padding: isMobile ? "14px 20px" : "16px 24px", background: "rgba(28,75,59,0.04)", borderRadius: 0 }}>
              <p style={{ fontSize: 11, color: "rgba(28,75,59,0.55)", lineHeight: 1.7, margin: 0, letterSpacing: 0.2 }}>
                {t("productDetail.substituteDisclaimer")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ COMPOSITION — Alternating rows with parallax ═══ */}
      {compositionItems.length > 0 && (
        <section style={{ padding: isMobile ? "56px 24px" : "96px 48px" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <div className="gs-cd-section-hdr" style={{ marginBottom: isMobile ? 32 : 56 }}>
              <div className="gs-cd-line" data-w="48" style={{ width: 48, height: 1, background: cfg.color_muted, marginBottom: 20 }} />
              <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: cfg.color_muted, margin: "0 0 10px" }}>
                {t("productDetail.containsTitle")}
              </p>
              <h2 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 24 : 38, fontWeight: 400, color: cfg.color_dark, margin: 0, lineHeight: 1.1, letterSpacing: -0.5 }}>
                {t("productDetail.insideThisBox")}
              </h2>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 40 : 0 }}>
              {compositionItems.map((item, i) => {
                const isOdd = i % 2 !== 0;
                return (
                  <div
                    key={i}
                    className="gs-cd-comp-row"
                    style={{
                      display: isMobile ? "block" : "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: isMobile ? 16 : 64,
                      alignItems: "center",
                      padding: isMobile ? 0 : "48px 0",
                      borderTop: !isMobile && i > 0 ? "1px solid rgba(0,0,0,0.05)" : "none",
                    }}
                  >
                    {/* Image with parallax */}
                    <Parallax speed={isMobile ? 0 : isOdd ? 4 : -4} style={{ order: !isMobile && isOdd ? 2 : 1 }}>
                      <div style={{ overflow: "hidden", aspectRatio: isMobile ? "16/10" : "4/3", background: cfg.color_light_bg, position: "relative" }}>
                        {item.image_url ? (
                          <img src={resolveImageUrl(item.image_url)} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.6s" }}
                            onMouseEnter={(e) => { (e.target as HTMLImageElement).style.transform = "scale(1.04)"; }}
                            onMouseLeave={(e) => { (e.target as HTMLImageElement).style.transform = "scale(1)"; }}
                          />
                        ) : (
                          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ddd" strokeWidth="1"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21,15 16,10 5,21" /></svg>
                          </div>
                        )}
                        {/* Number */}
                        <span style={{
                          position: "absolute", top: isMobile ? 10 : 16, left: isMobile ? 10 : 16,
                          fontSize: 9, fontWeight: 700, letterSpacing: 2, color: "#fff",
                          background: cfg.color_dark, padding: "4px 10px", textTransform: "uppercase",
                        }}>
                          {String(i + 1).padStart(2, "0")} / {compositionItems.length}
                        </span>
                      </div>
                    </Parallax>

                    {/* Text */}
                    <div style={{ order: !isMobile && isOdd ? 1 : 2, marginTop: isMobile ? 16 : 0 }}>
                      <div style={{ width: 24, height: 2, background: themeColor, marginBottom: 16 }} />
                      <h3 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 20 : 26, fontWeight: 400, color: cfg.color_dark, margin: "0 0 14px", lineHeight: 1.15, letterSpacing: -0.3 }}>
                        {item.name}
                      </h3>
                      {item.nutrition && item.nutrition.trim() && (
                        <div style={{
                          background: cfg.color_light_bg,
                          borderLeft: `3px solid ${themeColor}`,
                          padding: isMobile ? "12px 14px" : "14px 18px",
                          marginBottom: 18,
                          maxWidth: 440,
                        }}>
                          <p style={{
                            fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase",
                            color: themeColor, margin: "0 0 8px",
                          }}>
                            {t("productDetail.allergensNutrition")}
                          </p>
                          <RichText html={item.nutrition} style={{
                            fontSize: 12, color: "rgba(28,75,59,0.75)", lineHeight: 1.65,
                          }} />
                        </div>
                      )}
                      <RichText html={item.description} style={{ fontSize: 15, color: "rgba(28,75,59,0.86)", lineHeight: 1.85, maxWidth: 440 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ═══ INFO ACCORDIONS ═══ */}
      {(confezione.return_policy || confezione.shipping_info || confezione.allergens) && (
        <section className="gs-cd-info-section" style={{ borderTop: "1px solid rgba(0,0,0,0.06)", padding: isMobile ? "0 24px 40px" : "0 48px 64px" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            {[
              { title: t("productDetail.returnPolicy"), content: confezione.return_policy },
              { title: t("productDetail.shippingInfo"), content: confezione.shipping_info },
              { title: t("productDetail.allergens"), content: confezione.allergens },
            ].filter((s) => s.content).map((section, i) => (
              <InfoAccordion key={i} title={section.title} content={section.content!} cfg={cfg} isMobile={isMobile} />
            ))}
          </div>
        </section>
      )}

      {/* ═══ RELATED ═══ */}
      {related.length > 0 && (
        <section style={{ background: cfg.color_dark, padding: isMobile ? "64px 24px" : "96px 48px" }}>
          <div style={{ maxWidth: 1280, margin: "0 auto" }}>
            <div className="gs-cd-section-hdr" style={{ marginBottom: isMobile ? 28 : 40 }}>
              <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: "#fff", margin: "0 0 10px" }}>
                {t("collectionsIndex.giftBox")}
              </p>
              <h2 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 22 : 30, fontWeight: 400, color: "#fff", margin: 0, letterSpacing: -0.3 }}>
                {t("collectionDetail.youMightAlsoLike")}
              </h2>
            </div>

            <div className="gs-cd-related" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: isMobile ? 16 : 24, background: "transparent" }}>
              {related.slice(0, 3).map((r) => {
                const img = r.media.length > 0 ? resolveImageUrl(r.media[0].url) : null;
                return (
                  <Link
                    key={r.id}
                    to={productPath(locale, r.slug)}
                    style={{ textDecoration: "none", color: "inherit", display: "block", background: isMobile ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.02)", overflow: "hidden", transition: "background 0.3s" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isMobile ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.02)"; }}
                  >
                    <div style={{ aspectRatio: "4/5", overflow: "hidden", background: "rgba(255,255,255,0.02)" }}>
                      {img ? (
                        <img src={img} alt={r.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: 0.8, transition: "opacity .4s, transform .5s" }}
                          onMouseEnter={(e) => { (e.target as HTMLImageElement).style.opacity = "0.95"; (e.target as HTMLImageElement).style.transform = "scale(1.03)"; }}
                          onMouseLeave={(e) => { (e.target as HTMLImageElement).style.opacity = "0.8"; (e.target as HTMLImageElement).style.transform = "scale(1)"; }}
                        />
                      ) : (
                        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><rect x="3" y="3" width="18" height="18" rx="2" /></svg>
                        </div>
                      )}
                    </div>
                    <div style={{ padding: isMobile ? "12px" : "16px 20px" }}>
                      <h3 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 13 : 16, fontWeight: 400, color: "#fff", margin: "0 0 4px", lineHeight: 1.2 }}>{r.title}</h3>
                      <span style={{ fontSize: isMobile ? 12 : 14, color: "#fff", fontVariantNumeric: "tabular-nums" }}>{fmt(r.price_cents)} &euro;</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Back link ── */}
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: isMobile ? "32px 24px 64px" : "40px 48px 80px" }}>
        <Link
          to={pathFor("collections", locale)}
          style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 500, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(28,75,59,0.86)", textDecoration: "none", transition: "color .2s, gap .3s" }}
          onMouseEnter={(e) => { e.currentTarget.style.color = cfg.color_dark; e.currentTarget.style.gap = "12px"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "#999"; e.currentTarget.style.gap = "8px"; }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          {t("productDetail.backToCollections")}
        </Link>
      </div>
    </div>
  );
}
